import React from "react";
import { NavLink, useHistory, useParams } from "react-router-dom";
import { match } from "ts-pattern";

import { CTA } from "src/components/cta/cta";
import { Separator } from "src/components/separator/separator";
import { ReportEdge, School } from "src/generated/types";
import { Routes } from "src/utils/routes";
import { useDocumentTitle } from "src/utils/use_document_title";
import { ReportTabsCombined } from "../../../../components/report_tabs_combined/report_tabs_combined";

import { Practical } from "./sections/practical/practical";
import { Intellectual } from "./sections/intellectual/intellectual";
import { Nurture } from "./sections/nurture/nurture";
import { Heart } from "./sections/heart/heart";
import { Head } from "./sections/head/head";
import { Hands } from "./sections/hands/hands";
import { Overview } from "./sections/overview/overview";

import "../../report.scss";

/*
 * Types
 */

interface ReportDetailPageProps {
    readonly reports: Array<ReportEdge>;
    readonly school: School;
}

type SectionPath = "intellectual" | "nurture" | "overview" | "head" | "hands" | "heart" | "practical";

interface SectionUrlPath {
    readonly section: SectionPath;
}

/*
 * Main
 */

export const ReportPartialPage = (props: ReportDetailPageProps): React.ReactElement => {
    const { reports, school } = props;

    useDocumentTitle("Flourishing Faith Index");

    const history = useHistory();
    const { section } = useParams<SectionUrlPath>();

    const scrollAndPush = (toPush: string): void => {
        history.push(toPush);
        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    };

    const getLink = (section: string) => {
        return `/reports/${reports[0]?.node?.type}/${section}`;
    };

    const renderDomainLinks = (): React.ReactElement => {
        return (
            <>
                <NavLink className="step-link" exact to={getLink(Routes.section.intellectual)}>
                    Intellectual
                </NavLink>
                <NavLink className="step-link" exact to={getLink(Routes.section.nurture)}>
                    Nurture
                </NavLink>
                <NavLink className="step-link" exact to={getLink(Routes.section.practical)}>
                    Practical
                </NavLink>
                <NavLink className="step-link" exact to={getLink(Routes.section.head)}>
                    Head
                </NavLink>
                <NavLink className="step-link" exact to={getLink(Routes.section.hands)}>
                    Hands
                </NavLink>
                <NavLink className="step-link" exact to={getLink(Routes.section.heart)}>
                    Heart
                </NavLink>
            </>
        );
    };

    const renderDomain = (): React.ReactElement => {
        return match(section)
            .with("intellectual", () => {
                return (
                    <>
                        <Intellectual reports={reports} />
                        <CTA
                            color="blue"
                            icon="chart"
                            onClick={(): void => scrollAndPush(getLink(Routes.section.nurture))}
                            subtitle="Next:"
                            title="Explore the Nurture Domain"
                        />
                    </>
                );
            })
            .with("nurture", () => {
                return (
                    <>
                        <Nurture reports={reports} />
                        <CTA
                            color="blue"
                            icon="chart"
                            onClick={(): void => scrollAndPush(getLink(Routes.section.practical))}
                            subtitle="Next:"
                            title="Explore the Practical Domain"
                        />
                    </>
                );
            })
            .with("overview", () => {
                return (
                    <>
                        <Overview />
                        <CTA
                            color="blue"
                            icon="chart"
                            onClick={(): void => scrollAndPush(getLink(Routes.section.intellectual))}
                            subtitle="Next:"
                            title="Explore the Six Domains"
                        />
                    </>
                );
            })
            .with("practical", () => {
                return (
                    <>
                        <Practical reports={reports} />
                        <CTA
                            color="blue"
                            icon="chart"
                            onClick={(): void => scrollAndPush(getLink(Routes.section.head))}
                            subtitle="Next:"
                            title="Explore the Head Domain"
                        />
                    </>
                );
            })
            .with("head", () => {
                return (
                    <>
                        <Head reports={reports} />
                        <CTA
                            color="blue"
                            icon="chart"
                            onClick={(): void => scrollAndPush(getLink(Routes.section.hands))}
                            subtitle="Next:"
                            title="Explore the Hands Domain"
                        />
                    </>
                );
            })
            .with("hands", () => {
                return (
                    <>
                        <Hands reports={reports} />
                        <CTA
                            color="blue"
                            icon="chart"
                            onClick={(): void => scrollAndPush(getLink(Routes.section.heart))}
                            subtitle="Next:"
                            title="Explore the Heart Domain"
                        />
                    </>
                );
            })
            .with("heart", () => {
                return (
                    <>
                        <Heart reports={reports} />
                        <CTA
                            color="blue"
                            icon="chart"
                            onClick={(): void => scrollAndPush(getLink(Routes.section.intellectual))}
                            subtitle="Next:"
                            title="Explore the Intellectual Domain"
                        />
                    </>
                );
            })
            .exhaustive();
    };

    return (
        <>
            <div className="report-page-top">
                <ReportTabsCombined currentReports={reports} school={school} />
                <Separator />
                <div className="report-internal-navigation">
                    <div className="internal-nav-top-line">
                        <NavLink className="step-link" exact to={getLink(Routes.section.overview)}>
                            Overview
                        </NavLink>
                        <div className="clear" />
                    </div>
                    <div className="internal-nav-bottom-line">{renderDomainLinks()}</div>
                </div>
            </div>
            {renderDomain()}
            <div className="internal-nav-bottom-line center">{renderDomainLinks()}</div>
        </>
    );
};
